import JsPDF from 'jspdf';
import 'jspdf-autotable';

const loadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = (err) => {
      console.error(`Failed to load image at ${url}`, err);
      resolve(null);
    };
    img.src = url;
  });
};

export const generateHPFReport = async (submissions) => {
  const doc = new JsPDF();

  // Add a darker gray background for the header area to make logos pop
  doc.setFillColor(243, 244, 246); // Gray-200 (Darker than before)
  doc.rect(0, 0, 210, 32, 'F');

  // Load logos
  const logo1 = await loadImage('/logo.png');
  const logo2 = await loadImage('/HPF-logo.png');

  // Add logos with correct aspect ratios
  if (logo1) {
    const imgProps = doc.getImageProperties(logo1);
    const width = 30;
    const height = (imgProps.height * width) / imgProps.width;
    doc.addImage(logo1, 'PNG', 15, 8, width, height);
  }

  if (logo2) {
    const imgProps = doc.getImageProperties(logo2);
    const width = 45;
    const height = (imgProps.height * width) / imgProps.width;
    // Positioning HPF logo on the right
    doc.addImage(logo2, 'PNG', 150, 8, width, height);
  }

  // Header Line (Teal)
  doc.setDrawColor(0, 150, 136);
  doc.setLineWidth(1.2);
  doc.line(0, 32, 210, 32);

  // Title
  doc.setFontSize(22);
  doc.setTextColor(31, 41, 55); // Gray-800
  doc.setFont('helvetica', 'bold');
  doc.text('HPF Assessment Results Report', 105, 48, { align: 'center' });

  doc.setFontSize(10);
  doc.setTextColor(107, 114, 128); // Gray-500
  doc.setFont('helvetica', 'normal');
  doc.text(
    `Generated on: ${new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })}`,
    105,
    56,
    { align: 'center' }
  );

  // Stats Summary Cards
  const gradedCount = submissions.length;
  const passCount = submissions.filter((sub) => {
    const attr = sub.attributes || sub;
    return (attr.mcqScore || 0) + (attr.practicalScore || 0) >= 70;
  }).length;
  const failCount = gradedCount - passCount;

  // Summary box
  doc.setFillColor(249, 250, 251); // Gray-50
  doc.roundedRect(15, 65, 180, 25, 3, 3, 'F');

  doc.setFontSize(11);
  doc.setTextColor(75, 85, 99); // Gray-600
  doc.setFont('helvetica', 'bold');
  doc.text('Summary Statistics', 20, 72);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(`Total Candidates: ${gradedCount}`, 20, 82);
  doc.text(`Passed: ${passCount}`, 80, 82);
  doc.text(`Failed: ${failCount}`, 130, 82);
  doc.text(
    `Pass Rate: ${
      gradedCount > 0 ? Math.round((passCount / gradedCount) * 100) : 0
    }%`,
    165,
    82
  );

  // Table
  const tableData = submissions.map((sub) => {
    const attr = sub.attributes || sub;
    const mcqScore = attr.mcqScore || 0;
    const practicalScore = attr.practicalScore || 0;
    const finalScore = mcqScore + practicalScore;
    return [
      attr.fullName,
      attr.email,
      `${mcqScore}/25`,
      `${practicalScore}/75`,
      `${finalScore}%`,
      new Date(attr.createdAt).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }),
    ];
  });

  doc.autoTable({
    startY: 95,
    head: [
      ['Candidate', 'Email', 'MCQ Score', 'Practical', 'Final Grade', 'Date'],
    ],
    body: tableData,
    headStyles: {
      fillColor: [0, 150, 136],
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold',
      halign: 'center',
      cellPadding: 4,
    },
    bodyStyles: {
      fontSize: 9,
      halign: 'center',
      cellPadding: 3,
      textColor: [55, 65, 81],
    },
    columnStyles: {
      0: { halign: 'left', fontStyle: 'bold', textColor: [17, 24, 39] },
      1: { halign: 'left' },
      4: { fontStyle: 'bold' },
    },
    alternateRowStyles: { fillColor: [240, 253, 250] },
    margin: { top: 40, left: 15, right: 15 },
    didDrawPage: (data) => {
      const currentPage = data.pageNumber;
      const { height: pageHeight, width: pageWidth } = doc.internal.pageSize;

      // On pages after the first, add a mini header
      if (currentPage > 1) {
        // Light gray header background
        doc.setFillColor(243, 244, 246);
        doc.rect(0, 0, pageWidth, 32, 'F');

        // Add logos on continuation pages
        if (logo1) {
          const imgProps = doc.getImageProperties(logo1);
          const w = 22;
          const h = (imgProps.height * w) / imgProps.width;
          doc.addImage(logo1, 'PNG', 15, 8, w, h);
        }
        if (logo2) {
          const imgProps = doc.getImageProperties(logo2);
          const w = 34;
          const h = (imgProps.height * w) / imgProps.width;
          doc.addImage(logo2, 'PNG', 160, 8, w, h);
        }

        // Title on continuation pages
        doc.setFontSize(13);
        doc.setTextColor(31, 41, 55);
        doc.setFont('helvetica', 'bold');
        doc.text('HPF Assessment Results Report', pageWidth / 2, 16, {
          align: 'center',
        });

        doc.setFontSize(9);
        doc.setTextColor(107, 114, 128);
        doc.setFont('helvetica', 'normal');
        doc.text('(Continued)', pageWidth / 2, 22, { align: 'center' });

        // Teal divider line
        doc.setDrawColor(0, 150, 136);
        doc.setLineWidth(1.2);
        doc.line(0, 32, pageWidth, 32);
      }

      // Footer placeholder — final page count will be set after table is drawn
      doc.setFontSize(9);
      doc.setTextColor(156, 163, 175);
      doc.setFont('helvetica', 'normal');

      doc.text(`Page ${currentPage}`, pageWidth / 2, pageHeight - 10, {
        align: 'center',
      });
      doc.text(
        'Power Learn Project - HPF Assessment Confidential Report',
        15,
        pageHeight - 10
      );
    },
  });

  // Post-processing: update footer with correct "Page X of Y"
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    const { height: pageHeight, width: pageWidth } = doc.internal.pageSize;

    // White-out old page text and re-draw with total
    doc.setFillColor(255, 255, 255);
    doc.rect(pageWidth / 2 - 25, pageHeight - 14, 50, 8, 'F');

    doc.setFontSize(9);
    doc.setTextColor(156, 163, 175);
    doc.setFont('helvetica', 'normal');
    doc.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, {
      align: 'center',
    });
  }

  doc.save(
    `HPF_Assessment_Report_${new Date().toISOString().split('T')[0]}.pdf`
  );
};
